export interface ISpecialAssessment {
    [key: string]: any;
    id: string;
    legacyId?: string | number;
    year: number;
    amount: number;
    name?: string;
    clientRequested: boolean;
    legacyScalarSource?: 'sa' | 'currentSA';
}

export interface ISpecialAssessmentYearTotal {
    year: number;
    currentAmount: number;
    recommendedAmount: number;
}

export interface ILegacySpecialAssessmentRow {
    [key: string]: any;
    id?: string | number;
    year: string | number;
    amount: string | number;
    name?: string;
}

export interface ISerializedSpecialAssessments {
    specialAssessments: ILegacySpecialAssessmentRow[];
    specialAssessmentsCustomer: ILegacySpecialAssessmentRow[];
}

export interface ILegacyScalarClears {
    sa?: 0;
    currentSA?: 0;
}

export type SpecialAssessmentValidationMessage =
    'modals.special-assessments-worksheet.validation.name-required' |
    'modals.special-assessments-worksheet.validation.year-invalid' |
    'modals.special-assessments-worksheet.validation.amount-invalid';

type AssessmentSource = 'specialAssessments' | 'specialAssessmentsCustomer';

const normalizeRows = (value: any, source: AssessmentSource, clientRequested: boolean): ISpecialAssessment[] => {
    if (value === undefined || value === null) {
        return [];
    }
    if (!Array.isArray(value)) {
        throw new TypeError(`${source} must be an array`);
    }

    const rows = (value.length === 1 && Array.isArray(value[0])) ? value[0] : value;
    const usedIds: { [key: string]: number } = {};

    return rows.map((row: any, index: number) => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) {
            throw new TypeError(`${source}[${index}] must be an assessment object`);
        }

        const year = parseNumber(row.year, `${source}[${index}].year`);
        const amount = parseNumber(row.amount, `${source}[${index}].amount`);
        const legacyId = (typeof row.id === 'string' || typeof row.id === 'number') ? row.id : undefined;
        const idBase = (legacyId === undefined) ?
            `${source}:index:${index}` :
            `${source}:legacy:${typeof legacyId}:${String(legacyId)}`;
        const duplicateCount = usedIds[idBase] || 0;
        usedIds[idBase] = duplicateCount + 1;
        const id = duplicateCount === 0 ? idBase : `${idBase}:duplicate:${index}`;
        const normalized: ISpecialAssessment = {
            id,
            year,
            amount,
            clientRequested,
        };

        Object.keys(row).forEach((key: string) => {
            if (!isReservedField(key) && key !== 'id') {
                normalized[key] = row[key];
            }
        });

        if (legacyId !== undefined) {
            normalized.legacyId = legacyId;
        }
        if (Object.prototype.hasOwnProperty.call(row, 'name')) {
            normalized.name = row.name;
        }

        return normalized;
    });
};

const isReservedField = (key: string): boolean =>
    key === 'id' ||
    key === 'legacyId' ||
    key === 'year' ||
    key === 'amount' ||
    key === 'name' ||
    key === 'clientRequested' ||
    key === 'legacyScalarSource';

const parseNumber = (value: any, field: string): number => {
    if (typeof value === 'number' && isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (isFinite(parsed)) {
            return parsed;
        }
    }
    throw new TypeError(`${field} must be a finite number`);
};

const parseOptionalScalar = (value: any, field: string): number | undefined => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    return parseNumber(value, field);
};

const hasFirstYearAssessment = (rows: ISpecialAssessment[], beginYear: number): boolean =>
    rows.some((row: ISpecialAssessment) => row.year === beginYear);

const createScalarAssessment = (
    scalar: number,
    source: 'sa' | 'currentSA',
    beginYear: number,
    clientRequested: boolean,
): ISpecialAssessment => ({
    id: `scalar:${source}`,
    year: beginYear,
    amount: scalar,
    clientRequested,
    legacyScalarSource: source,
});

export const normalizeSpecialAssessments = (
    chart: any,
    beginYear: number,
    yearCount: number,
): ISpecialAssessment[] => {
    const companyRows = normalizeRows(chart && chart.specialAssessments, 'specialAssessments', false);
    const clientRows = normalizeRows(chart && chart.specialAssessmentsCustomer, 'specialAssessmentsCustomer', true);

    const recommendedScalar = parseOptionalScalar(chart && chart.sa, 'sa');
    if (recommendedScalar && !hasFirstYearAssessment(companyRows, beginYear)) {
        companyRows.unshift(createScalarAssessment(recommendedScalar, 'sa', beginYear, false));
    }

    const currentScalar = parseOptionalScalar(chart && chart.currentSA, 'currentSA');
    if (currentScalar && !hasFirstYearAssessment(clientRows, beginYear)) {
        clientRows.unshift(createScalarAssessment(currentScalar, 'currentSA', beginYear, true));
    }

    return companyRows.concat(clientRows);
};

export const aggregateSpecialAssessmentsByYear = (
    rows: ISpecialAssessment[],
    beginYear: number,
    yearCount: number,
): ISpecialAssessmentYearTotal[] => {
    const totals: ISpecialAssessmentYearTotal[] = [];
    for (let index = 0; index < yearCount; index++) {
        totals.push({
            year: beginYear + index,
            currentAmount: 0,
            recommendedAmount: 0,
        });
    }

    rows.forEach((row: ISpecialAssessment) => {
        const index = row.year - beginYear;
        if (!isFinite(row.year) || !isFinite(row.amount) || Math.floor(index) !== index || index < 0 || index >= totals.length) {
            return;
        }
        if (row.clientRequested) {
            totals[index].currentAmount += row.amount;
        } else {
            totals[index].recommendedAmount += row.amount;
        }
    });

    return totals;
};

const serializeRow = (row: ISpecialAssessment): ILegacySpecialAssessmentRow => {
    const serialized: any = {};

    Object.keys(row).forEach((key: string) => {
        if (!isReservedField(key)) {
            serialized[key] = row[key];
        }
    });

    if (row.legacyId !== undefined) {
        serialized.id = row.legacyId;
    }
    serialized.year = String(row.year);
    serialized.amount = String(row.amount);
    if (row.name !== undefined) {
        serialized.name = row.name;
    }

    return serialized;
};

export const serializeSpecialAssessments = (rows: ISpecialAssessment[]): ISerializedSpecialAssessments => {
    const result: ISerializedSpecialAssessments = {
        specialAssessments: [],
        specialAssessmentsCustomer: [],
    };

    rows.forEach((row: ISpecialAssessment) => {
        const serialized = serializeRow(row);
        if (row.clientRequested) {
            result.specialAssessmentsCustomer.push(serialized);
        } else {
            result.specialAssessments.push(serialized);
        }
    });

    return result;
};

export const getLegacyScalarClears = (
    originalRows: ISpecialAssessment[],
    currentRows: ISpecialAssessment[],
    beginYear: number,
): ILegacyScalarClears => {
    const result: ILegacyScalarClears = {};

    originalRows.forEach((originalRow: ISpecialAssessment) => {
        const source = originalRow.legacyScalarSource;
        if (!source) {
            return;
        }

        let currentRow: ISpecialAssessment | undefined;
        currentRows.forEach((row: ISpecialAssessment) => {
            if (row.id === originalRow.id) {
                currentRow = row;
            }
        });

        const unchanged = currentRow &&
            currentRow.legacyScalarSource === source &&
            currentRow.year === originalRow.year &&
            currentRow.year === beginYear &&
            currentRow.amount === originalRow.amount &&
            currentRow.name === originalRow.name &&
            currentRow.clientRequested === originalRow.clientRequested;

        if (!unchanged) {
            result[source] = 0;
        }
    });

    return result;
};

export const validateSpecialAssessmentDraft = (
    rows: ISpecialAssessment[],
    beginYear: number,
    yearCount: number,
    changedRowIds: string[],
): SpecialAssessmentValidationMessage | undefined => {
    const lastYear = beginYear + yearCount - 1;

    for (const row of rows) {
        if (changedRowIds.indexOf(row.id) < 0) {
            continue;
        }
        if (typeof row.name !== 'string' || row.name.trim() === '') {
            return 'modals.special-assessments-worksheet.validation.name-required';
        }
        if (!isFinite(row.year) || Math.floor(row.year) !== row.year || row.year < beginYear || row.year > lastYear) {
            return 'modals.special-assessments-worksheet.validation.year-invalid';
        }
        if (!isFinite(row.amount) || row.amount < 0) {
            return 'modals.special-assessments-worksheet.validation.amount-invalid';
        }
    }

    return undefined;
};



// WEBPACK FOOTER //
// ./src/report/special-assessments.ts